/*
 * Copyright (c) 2024 Ask Sage, Inc. All rights reserved.
 * This software is the confidential and proprietary information of Ask Sage, Inc.
 * You shall not disclose such confidential information and shall use it only in
 * accordance with the terms of the license agreement you entered into with Ask Sage, Inc.
 * THIS SOFTWARE IS PROVIDED "AS IS" AND ANY EXPRESSED OR IMPLIED WARRANTIES,
 * INCLUDING, BUT NOT LIMITED TO, THE IMPLIED WARRANTIES OF MERCHANTABILITY AND
 * FITNESS FOR A PARTICULAR PURPOSE ARE DISCLAIMED. IN NO EVENT SHALL ASK SAGE, INC.
 * OR ITS CONTRIBUTORS BE LIABLE FOR ANY DIRECT, INDIRECT, INCIDENTAL, SPECIAL,
 * EXEMPLARY, OR CONSEQUENTIAL DAMAGES (INCLUDING, BUT NOT LIMITED TO, PROCUREMENT
 * OF SUBSTITUTE GOODS OR SERVICES; LOSS OF USE, DATA, OR PROFITS; OR BUSINESS
 * INTERRUPTION) HOWEVER CAUSED AND ON ANY THEORY OF LIABILITY, WHETHER IN CONTRACT,
 * STRICT LIABILITY, OR TORT (INCLUDING NEGLIGENCE OR OTHERWISE) ARISING IN ANY WAY
 * OUT OF THE USE OF THIS SOFTWARE, EVEN IF ADVISED OF THE POSSIBILITY OF SUCH DAMAGE.
 * You may not use this software except in compliance with the License.
 * You may not modify, reverse engineer, or redistribute this software.
 */
import React, { useEffect, useRef, createRef, useState } from 'react';
import { isBotLoggedIn, getUser } from '../AuthService.js'
import { Stack, useTheme } from '@mui/material'
import { ChatMessage } from './ChatMessage.js';
import { ChatInputNew } from './SimpleChatInput/ChatInputNew.js';

// Primary Chat Window
const ChatBox = ({
    chatLog, 
    saveMemo,
    cur_user, 
    handleAudio, 
    handleSubmit, 
    clickHandler, 
    live, 
    handleLive, 
    clickHandlerDelete,
    clickHandlerEdit, 
    clickHandlerFollowUp, 
    clickHandlerCopy, 
    clickHandlerDown, 
    paid, 
    tokens_count, 
    isCUICapable, 
    getTokenWindow, 
    getTokenizer, 
    prevCommand, 
    setChatLogAndSaveWithScroll, 
    setCurrentPersona,
    personas, 
    chatInput, 
    setChatInputMode,
    hideSidebar,
    hideSideMemoBar,
    handlePluginClick,
    handleDetailsClick,
    sources
  }) => {
  const messageRefs = useRef([]);
  messageRefs.current = chatLog.map((_, i) => messageRefs.current[i] ?? createRef());
  const isLastMeMessage = chatLog.map((message, index) => message.user === "me" ? index : -1).reduce((a, b) => Math.max(a, b), -1);
  const isLastSageMessage = chatLog.map((message, index) => message.user === "gpt" ? index : -1).reduce((a, b) => Math.max(a, b), -1);
  const [chatInputPass, setChatInputPass] = useState('');
  const theme = useTheme();

  useEffect(() => {
    if (isLastSageMessage !== -1) {
      messageRefs.current[isLastSageMessage].current.scrollIntoView({ behavior: 'instant' });
    }
  }, [chatLog]);

  function getWidth() {
    if (hideSidebar && hideSideMemoBar) {
      return 'calc(100vw - 100px)';
    } else if (hideSidebar) {
      return 'calc(100vw - 310px)';
    } else if (hideSideMemoBar) {
      return 'calc(100vw - 310px)';
    } else {
      return 'calc(100vw - 520px)';
    }
  }

  return (
    <Stack
      className="chatbox"
      sx={{
        height: '100%',
        width: '100%',
        maxWidth: getWidth(),
        backgroundColor: theme.palette.chatBackground,
      }}
    >
      <Stack
        sx={{
          overflow: 'auto',
          flexGrow: 1,
          flexDirection: 'column',
          paddingX: 1,
          paddingTop: 2,
          width: '100%',
          paddingBottom: 5,
        }}
        className={`${isBotLoggedIn() ? 'bot' : ''} chat-log`}
      >
        {chatLog.map((message, index) => (
          <div ref={messageRefs.current[index]} key={`message-${index}`}>
            <ChatMessage
              saveMemo={saveMemo}
              clickHandlerEdit={clickHandlerEdit}
              isLastMeMessage={index === isLastMeMessage}
              isLastSageMessage={index === isLastSageMessage}
              key={index}
              index={index}
              message={message}
              clickHandlerFollowUp={clickHandlerFollowUp}
              clickHandler={clickHandler}
              clickHandlerCopy={clickHandlerCopy}
              clickHandlerDelete={clickHandlerDelete}
              clickHandlerDown={clickHandlerDown}
              setChatInput={setChatInputPass}
            />
          </div>
        ))}
      </Stack>
      <ChatInputNew
        getTokenizer={getTokenizer}
        handleSubmit={handleSubmit}
        prevCommand={prevCommand}
        setCurrentPersona={setCurrentPersona}
        handleLive={handleLive}
        live={live}
        paid={paid}
        personas={personas}
        cur_user={cur_user}
        handleAudio={handleAudio}
        setChatLogAndSaveWithScroll={setChatLogAndSaveWithScroll}
        tokens_count={tokens_count}
        getTokenWindow={getTokenWindow}
        isCUICapable={isCUICapable}
        chatInputPass={chatInputPass}
        setChatInputPass={setChatInputPass}
        setChatInputMode={setChatInputMode}
        handlePluginClick={handlePluginClick}
        handleDetailsClick={handleDetailsClick}
        sources={sources}
      />
    </Stack>

  );
}

export { ChatBox };